import { createContext, useContext, useEffect, useState, useCallback } from "react";
import api, { getToken, setToken, clearToken, SCOPE_KEY } from "@/lib/api";

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);   // null = loading, false = not authed, {...} = authed
  const [ready, setReady] = useState(false);
  const [scopedOperator, setScopedOperatorState] = useState(() => sessionStorage.getItem(SCOPE_KEY) || "");

  const setScopedOperator = useCallback((op) => {
    if (op) sessionStorage.setItem(SCOPE_KEY, op);
    else sessionStorage.removeItem(SCOPE_KEY);
    setScopedOperatorState(op || "");
    // Notify all pages that scope changed so they reload data
    window.dispatchEvent(new Event("crm:data-changed"));
  }, []);

  const check = useCallback(async () => {
    // If returning from Google OAuth, let AuthCallback handle it first
    if (typeof window !== "undefined" && window.location.hash?.includes("session_id=")) {
      setReady(true);
      return;
    }
    try {
      const r = await api.get("/auth/me");
      setUser(r.data);
      // On first login of a switchable user, seed with their default operator
      if (r.data?.can_switch_operator && !sessionStorage.getItem(SCOPE_KEY) && r.data?.operatore) {
        sessionStorage.setItem(SCOPE_KEY, r.data.operatore);
        setScopedOperatorState(r.data.operatore);
      }
    } catch {
      setUser(false);
    } finally {
      setReady(true);
    }
  }, []);

  useEffect(() => { check(); }, [check]);

  const login = async (username, password) => {
    const r = await api.post("/auth/login", { username, password }, { timeout: 15000 });
    // If backend requires a PIN as 2nd factor, don't set token yet — the caller must call verifyPin.
    if (r.data?.step === "pin_required") {
      return r.data; // { step, pin_token, expires_in_seconds }
    }
    setToken(r.data.access_token);
    setUser({ auth_type: "shared", username: r.data.username });
    return r.data;
  };

  const verifyPin = async (pinToken, pin) => {
    const r = await api.post(
      "/auth/verify-pin",
      { pin_token: pinToken, pin },
      { timeout: 15000 },
    );
    setToken(r.data.access_token);
    setUser({ auth_type: "shared", username: r.data.username });
    return r.data;
  };

  const logout = async () => {
    try { await api.post("/auth/logout"); } catch { /* ignore */ }
    clearToken();
    setUser(false);
    window.location.href = "/login";
  };

  return (
    <AuthContext.Provider value={{ user, ready, login, verifyPin, logout, refresh: check, scopedOperator, setScopedOperator }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used inside AuthProvider");
  return ctx;
}
